export const CHECKOUT_URL = "https://rhinoplasty-en.impultienda.ar/checkout/";

export const images = {
  mainMockup: "https://cdn.magicpatterns.com/uploads/7iWqoCRxrMefD3i5Rk4dUP/RHINOPLASTY-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/nmoStY1G1Ds9AijXugZUow/Ethan_Whitmore_-_RHINOPLASTY.png",
};

export const stats = [
  { value: "6", label: "Premium volumes" },
  { value: "5", label: "Professional tools" },
  { value: "50+", label: "Educational cases & scenarios" },
  { value: "15 min", label: "To complete the MasterMap" },
];

export const beforeItems = [
  "You analyze every case from scratch.",
  "You jump between notes, photographs, courses, and different references.",
  "You have too many variables competing for your attention.",
  "You struggle to turn observations into clear objectives.",
  "Every case seems to require a completely different thought process.",
  "After studying a technique, you still have to mentally organize everything before applying it.",
];

export const afterItems = [
  "You analyze every case using a defined sequence.",
  "You organize your observations in one place.",
  "You separate problems, objectives, and strategic considerations.",
  "You use visual maps to structure your reasoning.",
  "You document your hypotheses and questions before moving forward.",
  "You review your analysis afterward and identify what you learned.",
];

export const painPoints = [
  { icon: "alert", text: "You have a complex case in front of you and know there are multiple variables to evaluate, but you're not sure what to analyze first." },
  { icon: "clock", text: "You spend too much time going back through notes, courses, and references before you can structure a case." },
  { icon: "help", text: "You have substantial rhinoplasty knowledge, but turning it into a repeatable analysis process is still difficult." },
  { icon: "books", text: "You've studied multiple techniques and approaches, but every source organizes the information differently." },
  { icon: "brain", text: "You feel like you have to keep too many things in your head at the same time when analyzing a case." },
  { icon: "frown", text: "After studying a case, it's hard to identify exactly what you learned and how to approach the next one more systematically." },
] as const;

export const masterFlow = ["Patient", "Observation", "Problem", "Objective", "Strategy", "Review"];

export const volumes = [
  {
    number: "I",
    title: "The Rhinoplasty MasterMap™",
    subtitle: "The core system",
    body: "My visual framework to organize an educational case without getting lost in dozens of competing variables.",
    bullets: [] as string[],
    flow: true,
  },
  {
    number: "II",
    title: "Facial Analysis",
    subtitle: "Learn to see before you decide",
    body: "A visual framework to turn scattered observations into organized information.",
    bullets: ["Facial proportions", "Symmetry", "Profile", "Frontal view", "Nose-to-face relationships", "Aesthetic characteristics", "Photography", "Educational objectives"],
    flow: false,
  },
  {
    number: "III",
    title: "Nasal Anatomy",
    subtitle: "Your visual map of key structures",
    body: "A visual reference system for the key anatomical structures relevant to rhinoplasty analysis.",
    bullets: ["Visual anatomy maps", "Terminology", "Anatomical relationships", "Structure-by-structure references", "Quick-reference sheets"],
    flow: false,
  },
  {
    number: "IV",
    title: "Problem → Objective → Strategy",
    subtitle: "One of the most important parts of the system",
    body: "Separate three things that are often mixed together, with matrices and exercises to practice the sequence across scenarios.",
    bullets: [] as string[],
    flow: false,
    steps: ["What do I observe?", "What is the educational objective?", "What strategy or information do I need to consider?"],
  },
  {
    number: "V",
    title: "The Surgical Decision Map",
    subtitle: "Structure the right questions",
    body: "Visual decision maps to structure the questions you should ask before reaching a professional decision — not one 'correct' answer for every patient.",
    bullets: ["Dorsum", "Tip", "Middle vault", "Asymmetry", "Function", "Planning", "Revision", "Prioritization"],
    flow: false,
  },
  {
    number: "VI",
    title: "Case Review",
    subtitle: "Turn every case into learning",
    body: "A structured framework so every educational case improves your own reasoning process.",
    bullets: ["What you observed", "Objectives you identified", "Options you considered", "Missing information", "What you learned", "What you'd analyze differently"],
    flow: false,
  },
];

export const tools = [
  { icon: "map", title: "MasterMap Quick Sheet", text: "A condensed version of the entire system to print and keep as a quick reference." },
  { icon: "grid", title: "Problem–Objective–Strategy Matrix", text: "A visual matrix that separates observations, objectives, and strategic considerations." },
  { icon: "tree", title: "Decision Trees", text: "Visual frameworks for structuring questions around different educational scenarios." },
  { icon: "chart", title: "Case Analysis Scorecard", text: "See which areas of a case you've analyzed and which still need attention." },
  { icon: "camera", title: "Photography & Documentation Planner", text: "A guide for organizing visual documentation for educational case review." },
] as const;

export const bonuses = [
  {
    number: 1,
    title: "50 Educational Cases to Practice the MasterMap™",
    value: 37,
    text: "50 educational case scenarios with analysis questions, problem identification, objective-setting prompts, and space to structure your reasoning.",
    image: "https://cdn.magicpatterns.com/uploads/fohzkwj6EpNXJ1po4gvgTq/BONUS_1_RHINOPLASTY-EN.png",
  },
  {
    number: 2,
    title: "Rhinoplasty Analysis in 15 Minutes™",
    value: 27,
    text: "My quick-start protocol for working through the MasterMap in a structured 15-minute session. Perfect for building a repeatable study habit.",
    image: "https://cdn.magicpatterns.com/uploads/9PReDMjJyXS8B2NtbAsiZ6/BONUS_2_RHINOPLASTY-EN.png",
  },
  {
    number: 3,
    title: "Rhinoplasty Error Atlas™",
    value: 37,
    text: "A visual library of common analysis pitfalls and critical points that can be overlooked. Train yourself to spot what still needs review.",
    image: "https://cdn.magicpatterns.com/uploads/nEMEAaJCTNBx6kRNLFv2Up/BONUS_3_RHINOPLASTY-EN.png",
  },
  {
    number: 4,
    title: "100 Surgical Decision Questions™",
    value: 29,
    text: "Questions designed to challenge your reasoning across educational scenarios — for individual study or case discussions with colleagues.",
    image: "https://cdn.magicpatterns.com/uploads/bgzfgQj2fANcSFgRoXU4YF/BONUS_4_RHINOPLASTY-EN.png",
  },
  {
    number: 5,
    title: "30 MasterMap Professional Checklists",
    value: 17,
    text: "Printable checklists for every stage of case analysis and review. Use them digitally or print them for your study workflow.",
    image: "https://cdn.magicpatterns.com/uploads/pp9WPsMv2CpqnAY6joQrrC/BONUS_5_RHINOPLASTY-EN.png",
  },
];

export const offerStack = [
  { title: "Rhinoplasty MasterMap™", detail: "6 Premium Volumes", value: 97 },
  { title: "5 Exclusive Bonuses", detail: "Cases, protocol, atlas, questions & checklists", value: 147 },
];

export const offerPerks = ["One-time payment", "Lifetime access", "No monthly fees", "Instant download"];

export const guaranteeItems = [
  "Open all 6 volumes",
  "Review the visual maps",
  "Use the MasterMap",
  "Work through the educational cases",
  "Test the checklists",
  "Use the matrices",
  "Explore all 5 bonuses",
];

export const testimonials = [
  {
    name: "Dr. Michael R.",
    country: "United States",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/b936f35c-ecd1-4b1f-9441-773204d64a77.jpg",
    quote: "What I liked most is that it doesn't try to replace what I've already studied. It gives me a structure for organizing it when I'm analyzing a case.",
  },
  {
    name: "Dr. James P.",
    country: "United Kingdom",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/861bb0dc-f4a8-4fe1-ba58-ada00e77c95a.jpg",
    quote: "The MasterMap helped me stop jumping between different references during my study sessions. I now have a sequence I can follow.",
  },
  {
    name: "Dr. Laura M.",
    country: "Mexico",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/c0a5857f-9cb2-4a1f-bcff-e7cfd895eccb.jpg",
    quote: "The Problem–Objective–Strategy matrix is probably the tool I use the most. It forces me to separate what I'm observing from what I'm trying to achieve.",
  },
  {
    name: "Dr. Andrew C.",
    country: "Canada",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/b057bf18-fbc8-4110-8778-5fcb35abe6fd.jpg",
    quote: "The educational cases make the system much more practical. It's not just another document that you read and forget.",
  },
  {
    name: "Dr. Daniel S.",
    country: "Spain",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/3fce97e2-0ff7-40fc-b3fd-a6e548a5185b.jpg",
    quote: "I didn't buy another course. I bought a tool I can open whenever I want to study a case.",
  },
];

export const authorQuestions = [
  "Where do I start?",
  "What should I analyze first?",
  "Which information actually matters?",
  "How do I organize my observations?",
  "What questions haven't I answered yet?",
];

export const faqs = [
  { q: "How will I receive the material?", a: "Immediately after your purchase, you'll receive access to download all 6 volumes and 5 bonuses in digital format." },
  { q: "Who is Rhinoplasty MasterMap™ for?", a: "It's designed as an educational resource for plastic surgeons, otolaryngologists, facial plastic surgeons, and other appropriately trained medical professionals involved in rhinoplasty." },
  { q: "Is this a surgical training course?", a: "No. Rhinoplasty MasterMap™ is an educational system for case analysis, planning, and review. It does not replace formal surgical training, clinical supervision, institutional protocols, current medical literature, or professional judgment." },
  { q: "Do I need previous rhinoplasty training?", a: "Yes. The system is designed for medical professionals who already have appropriate medical education and foundational rhinoplasty knowledge and want to structure their case analysis and learning process." },
  { q: "Are the files downloadable?", a: "Yes. You'll receive downloadable digital materials for your personal reference." },
  { q: "Do I get lifetime access?", a: "Yes. Your purchase includes lifetime access to the materials included with your order." },
  { q: "Is this a one-time payment?", a: "Yes. You pay $27 once. There is no required monthly subscription." },
  { q: "Can I print the materials?", a: "Yes. The MasterMaps, matrices, checklists, and worksheets are designed to work both digitally and in print." },
  { q: "What if I don't like it?", a: "You have a 7-day guarantee. You can review the material and request a refund during the guarantee period according to the purchase terms." },
  { q: "Does the system guarantee a surgical outcome?", a: "No. No educational tool can guarantee a specific clinical or surgical outcome. Rhinoplasty MasterMap™ helps you structure your educational analysis and study process. It does not replace professional judgment, formal training, clinical experience, or applicable medical protocols." },
];

export const recentBuyers = [
  { name: "Dr. Alejandro M.", country: "Mexico" },
  { name: "Dra. Valentina R.", country: "Colombia" },
  { name: "Dr. Joaquín P.", country: "Argentina" },
  { name: "Dra. Camila S.", country: "Chile" },
  { name: "Dr. Mateo G.", country: "Spain" },
  { name: "Dra. Lucía F.", country: "Peru" },
  { name: "Dr. Santiago V.", country: "Uruguay" },
  { name: "Dra. Isabela C.", country: "Costa Rica" },
  { name: "Dr. Diego H.", country: "Ecuador" },
  { name: "Dra. Mariana L.", country: "Dominican Republic" },
  { name: "Dr. Andrés T.", country: "Guatemala" },
  { name: "Dra. Sofía B.", country: "Panama" },
];
